/**
 * The markup the footer editor's quick buttons put in (SB-13366): the site link, the school logo, and the
 * SchoolsBuddy default footer made of the two.
 *
 * Written in exactly the form TinyMCE serialises it — tags, attribute order, escaping and the line
 * break between blocks — because saving runs the footer through the server's sanitiser and the editor
 * then compares what it sent with what came back. Any difference shows the author the "was cleaned up"
 * warning on every save of the default. `EmailFooterTemplateHtmlSanitizerTests` pins the same strings
 * on the server side; keep the two in step.
 *
 * Each block is a paragraph centred with the `align` attribute, not `text-align`: the sanitiser rewrites
 * an inline style (it drops TinyMCE's trailing semicolon), which would bring the warning back. TinyMCE's
 * html5 schema and the sanitiser both keep `align`, and mail programs honour it.
 */

/** The widest the inserted logo is drawn, in pixels (criterion 5.2: "approx 250 pixels width"). */
export const FOOTER_LOGO_MAX_WIDTH = 250;

/** A logo's natural size, when it could be measured. */
export interface FooterLogoSize {
  width: number;
  height: number;
}

/** The school logo to put in the default footer. */
export interface FooterLogo {
  url: string;
  size?: FooterLogoSize | null;
}

/** Escapes text content the way TinyMCE and the sanitiser both write it: quotes stay as they are. */
export function escapeFooterText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Escapes a double-quoted attribute value the way TinyMCE and the sanitiser both write it. */
export function escapeFooterAttribute(value: string): string {
  return escapeFooterText(value).replace(/"/g, '&quot;');
}

/** The "Insert link to SchoolsBuddy Site" block: a centred link to the school's own SchoolsBuddy site. */
export const FOOTER_LINK_HTML = '<p align="center"><a href="~~{SITEURL}~~">Go to SchoolsBuddy Website</a></p>';

/**
 * The criterion 5.2 logo, centred: at most {@link FOOTER_LOGO_MAX_WIDTH} pixels wide and never enlarged.
 * Width and height are attributes, which Outlook honours where it ignores CSS; when the natural size is
 * not known, the width alone keeps the proportions. The school name is the alternative text, which is
 * what readers see while their mail program blocks images.
 */
export function buildFooterLogoHtml(
  url: string,
  organisationName: string | null | undefined,
  size?: FooterLogoSize | null
): string {
  const known = !!size && size.width > 0 && size.height > 0;
  const width = known ? Math.min(FOOTER_LOGO_MAX_WIDTH, size.width) : FOOTER_LOGO_MAX_WIDTH;
  const height = known ? ` height="${Math.round((size.height * width) / size.width)}"` : '';
  const alt = escapeFooterAttribute((organisationName ?? '').trim());

  return `<p align="center"><img src="${escapeFooterAttribute(url)}" alt="${alt}" width="${width}"${height}></p>`;
}

/** The whole default footer: the site link, then the logo when the school has one. */
export function buildDefaultFooterHtml(organisationName: string | null | undefined, logo?: FooterLogo | null): string {
  return [FOOTER_LINK_HTML, logo?.url ? buildFooterLogoHtml(logo.url, organisationName, logo.size) : null]
    .filter((block): block is string => !!block)
    .join('\n');
}
